<template>
  <div class="modal-overflow" style="min-height: 400px;">
    <table class="bulk-tasks">
      <colgroup>
        <col style="width:17%">
        <col style="width:16%">
        <col style="width:21%">
        <col style="width:20%">
        <col style="width:9%">
        <col v-if="anyTaskHasStartDateField" style="width: 8%">
        <col style="width:8%">
      </colgroup>
      <tr>
        <th>Name</th>
        <th>Status</th>
        <th>Notes</th>
        <th>Assignee</th>
        <th>PO Number</th>
        <th v-if="anyTaskHasStartDateField">Start Date</th>
        <th>Due Date</th>
      </tr>
      <tr v-for="task in tasks">
        <td style="padding-left: 8px;">
          <span class="invisible">{{ task.id }}</span>
          <a href="#" title="View/Edit Task" @click.prevent="handleEditTask(task.id)">{{
              getTaskNameWithOptionalRepeatCount(task)
            }}</a>
          <el-tooltip v-if="task.fieldValues && Object.keys(task.fieldValues).length !== 0" class="item" effect="dark"  placement="right-end">
            <div slot="content">
              <div style="font-weight: bold; margin-bottom: 7px;">Extra Fields</div>
              <div v-for="(value, key) in task.fieldValues" v-if="value" style="margin-bottom: 2px;"><strong>{{ formatKey(task, key) }}</strong>: {{ value }}<br/></div>
            </div>
            <img class="icon-help m-left-4" src="static/img/icon-list.png"/>
          </el-tooltip>
        </td>
        <td>
          <el-select name="status"
                     class="select"
                     size="large"
                     style="width: 86%"
                     data-vv-as="status"
                     placeholder="Task Status"
                     :disabled="!canEditStatus(task)"
                     @change="saveOne(task)"
                     v-model="task.status">
            <el-option v-for="option in statuses(task)"
                       class="select"
                       :value="option.value"
                       :label="option.label"
                       :key="option.label">
            </el-option>
          </el-select>
          <i v-if="task.completeLock" class="fa fa-lock" style="margin-left: 13px;" aria-hidden="true"></i>
        </td>
        <td style="word-break: break-all;">
          <div class="row" style="margin-right: -50px;">
            <div class="col-md-8" style="margin-top: 11px;">
              {{ viewNotesNoHtml(task.notes) }}
            </div>
            <div class="col-md-2" style="padding: 8px;">
              <a @click.prevent="editTaskNote(task)" href="#">
                <img src="static/img/icon-pencil.png" title="Edit Notes" class="pull-right" />
              </a>
            </div>
          </div>
        </td>
        <td>
          <v-select
            :value="task.assignee"
            placeholder="Assignee"
            @input="assignee => assignAssignee(task, assignee)"
            :disabled="!canEditAssignee(task.status)"
            label="label"
            code="value"
            :options="users"
            class="select-assignee">
          </v-select>
        </td>
        <td>
          <div v-if="task.name.startsWith(constants.TASKS.NAMES.INVOICE_SUBMITTAL)">
            <el-input
              v-model="task.fieldValues.poNumber"
              :disabled="!canEditPoNumber(task.status)"
              @change="maybeUpdateLinkedTaskPoNumber(task)"
              maxlength="25" />
            <!-- DO NOT adjust maxlength. QuickBooks limit restriction: IQBStringType	25 chars -->
          </div>
          <div v-else>
            <el-input
              v-model="task.fieldValues.poNumber"
              @change="saveOne(task)"
              :disabled="true" />
          </div>
        </td>
        <td v-if="anyTaskHasStartDateField">
          <el-date-picker
            v-if="!isInvoiceTask(task) && hasField(task, 'startDate')"
            type="date"
            style="width: 140px; margin: 0 4px;"
            :disabled="!canEditStartDate(task.status)"
            v-model="task.fieldValues.startDate"
            :format="constants.DATE.FORMAT.DISPLAY"
            :value-format="constants.DATE.FORMAT.VALUE"
            :picker-options="datePickerConfig"
            :clearable=false
            @change="saveOne(task)"
            placeholder="Start Date">
          </el-date-picker>
        </td>
        <td>
          <el-date-picker
            type="date"
            style="width: 140px; margin: 0 4px;"
            :disabled="!canEditDueDate(task.status)"
            v-model="task.dueDate"
            :format="constants.DATE.FORMAT.DISPLAY"
            :picker-options="datePickerConfig"
            :clearable=false
            @change="saveMaybeWarn(task)"
            placeholder="Due Date">
          </el-date-picker>
        </td>
      </tr>
    </table>
    <div class="row" style="margin-right: 0;" v-if="canEdit">
      <div class="col-md-10" style="margin-top: 12px;">
        <span v-if="!arePoNumbersValid" class="text-danger">
          <img src="static/img/icon-warning.png" style="width: 20px; height: 20px;" alt="Warning: At least one PO Number is invalid" />
          P.O. Numbers must only contain only numbers, letters, hyphens, periods, forward slashes, underscores, and spaces and have a maximum of 25 characters.
        </span>
      </div>
      <div class="col-md-2">
        <button type="submit" :disabled="taskCompletedOrCancelled || isSubmitDisabled || !arePoNumbersValid" @click.prevent="saveAll" class="btn btn-fill btn-info btn-wd pull-right">Save</button>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import {OK} from 'http-status-codes'
import Constants from 'src/utilities/constants'
import Permissions from 'src/utilities/permissions'
import {
  formatTaskFieldKey,
  notifyError,
  notifySuccess,
  notifyWarning,
  objectBy,
  objectById,
  stripHTML,
  taskNameWithOptionalRepeatCountSuffix,
  warnIfDueDateIsDayOff
} from 'src/utilities/utils'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import datePickerConfig from '@/components/UIComponents/DatePicker/datePickerConfig'
import _ from 'lodash'

export default {
  props: ['data'],
  components: {
    vSelect
  },
  computed: {
    canEdit() {
      return this.$can('edit', Permissions.TASKS)
    },
    isSubmitDisabled() {
      return this.isProcessing
    },
    anyTaskHasStartDateField() {
      for (const task of this.tasks) {
        if ((task.template && task.template.fields.find(f => f.key === 'startDate'))
          || task.fieldValues.hasOwnProperty('startDate')) {
          return true
        }
      }
      return false
    },
    ...mapGetters({
      userId: 'security/userId',
      users: 'users/kvpsForSelect',
      usersDeactivated: 'users/kvpsForSelectDeactivated',
      taskTemplates: 'tasks/templates/results',
    })
  },
  data () {
    return {
      datePickerConfig,
      constants: Constants,
      isProcessing: false,
      taskCompletedOrCancelled: false,
      tasks: [],
      job: null,
      client: null,
      arePoNumbersValid: true
    }
  },
  methods: {
    hasField(task, fieldKey) {
      return !!((task.template && task.template.fields.find(f => f.key === fieldKey))
        || task.fieldValues.hasOwnProperty(fieldKey))
    },
    isInvoiceTask(task) {
      return task.name.startsWith(Constants.TASKS.NAMES.INVOICE)
    },
    checkPONumbers() {
      for (let i = 0; i < this.tasks.length; i++) {
        const task = this.tasks[i]

        if (task.name && task.name.startsWith(Constants.TASKS.NAMES.INVOICE)) {
          const poNumber = _.get(task, 'fieldValues.poNumber')

          if (poNumber && poNumber.length > 0 && !Constants.REGEX.PO_REGEX.test(poNumber)) {
            this.arePoNumbersValid = false
            return false
          }
        }
      }
      this.arePoNumbersValid = true
      return true
    },
    statuses(task) {
      const statuses = this.$store.getters['statuses/ptracTask']

      // if completeLock is enabled OR
      // if the current task is not assigned to the current user AND the current user doesn't have permission to complete tasks for other users
      if (task.completeLock || (this.userId !== task.assigneeId
        && this.$can('edit', Permissions.COMPLETE_TASKS.NOT_ASSIGNED_TO_USER) === false)) {
        // return all statuses except 'Completed' && 'Cancelled'

        return _.filter(statuses, (statusKvp) => statusKvp.value !== Constants.STATUSES.COMPLETED && statusKvp.value !== Constants.STATUSES.CANCELLED)
      }

      // don't let users complete tasks via this interface - it skips required field validation that is implemented in EditTask modal
      return _.filter(statuses, (statusKvp) => statusKvp.value !== Constants.STATUSES.COMPLETED && statusKvp.value !== Constants.STATUSES.CANCELLED)
    },
    getTaskNameWithOptionalRepeatCount(task) {
      return taskNameWithOptionalRepeatCountSuffix(task)
    },
    canEditStatus(task) {
      if (task.completionDate || task.completeLock) {
        return false
      }

      return this.$can('edit', Permissions.TASKS) || this.userId === task.assigneeId
    },
    formatKey(task, fieldKey) {
      return formatTaskFieldKey(this.taskTemplates, task, fieldKey)
    },
    viewNotesNoHtml(notes) {
      if (notes) {
        return stripHTML(notes)
      }
      return null
    },
    editTaskNote(task) {
      this.$eventHub.$emit('show-modal-4', 'Task Note', task, '700px')
    },
    async appendNote() {
      this.isProcessing = true
      await this.loadTasksFromJob()
      this.isProcessing = false
      this.$forceUpdate()
    },
    assignAssignee(task, assignee) {
      task.assignee = null
      task.assigneeId = null
      if (assignee) {
        task.assignee = assignee.label
        task.assigneeId = assignee.value
        warnIfDueDateIsDayOff(this, task)
      }

      this.saveOne(task)
    },
    canEditAssignee(status) {
      return this.$can('edit', Permissions.ASSIGN_TASKS)
        && Constants.STATUSES.CANCELLED !== status
        && Constants.STATUSES.COMPLETED !== status
    },
    canEditDueDate(status) {
      return this.$can('edit', Permissions.TASK_DUE_DATE)
        && Constants.STATUSES.CANCELLED !== status
        && Constants.STATUSES.COMPLETED !== status
    },
    canEditPoNumber(status) {
      return this.$can('edit', Permissions.TASKS)
        && Constants.STATUSES.CANCELLED !== status
        && Constants.STATUSES.COMPLETED !== status
    },
    canEditStartDate(status) {
      return this.$can('edit', Permissions.TASKS)
        && Constants.STATUSES.CANCELLED !== status
        && Constants.STATUSES.COMPLETED !== status
    },
    saveMaybeWarn(task) {
      this.saveOne(task)
      warnIfDueDateIsDayOff(this, task)
    },
    async saveOne(task) {
      this.isProcessing = true
      delete task.template

      const response = await this.$store.dispatch('save2', {
        endpoint: 'tasks',
        data: task
      })

      this.isProcessing = false

      if (OK !== response.status) {
        notifyError(this, 'Failed to update task')
        return
      }

      this.updateTask(response.data)
    },
    async saveAll() {
      this.isProcessing = true

      const tasks = _.filter(this.tasks, (task) => !task.completionDate)

      const responses = []
      for (const task of tasks) {
        delete task.template
        responses.push(await this.$store.dispatch('save2', {
          endpoint: 'tasks',
          data: task
        }))
      }

      this.handleTasksSaved(responses)
      this.isProcessing = false
    },
    handleTasksSaved(responses) {
      if (responses.length === 0) {
        notifySuccess(this, 'Tasks up to date')
        this.$emit('close')
      }
      if (Array.isArray(responses)) {
        let errorSavingTask = false
        for (const response of responses) {
          if (response.status === 200) {
            notifySuccess(this, response.data.name + ' saved successfully')
          } else {
            notifyError(this, null, response.errors)
            errorSavingTask = true
          }
        }

        if (!errorSavingTask) {
          this.$emit('close')
        }
      }
    },
    handleEditTask(taskId) {
      this.$eventHub.$emit('show-modal-3', 'Edit Task', {id: taskId})
    },
    async loadJobAndClient() {
      const jobResponse = await this.$store.dispatch('fetchById', {
        endpoint: 'jobs',
        id: this.data.jobId
      })

      if (OK !== jobResponse.status) {
        notifyError(this, 'Failed to load Job. Please refresh your screen and try again.')
        return
      }

      this.job = jobResponse.data

      if (this.job.clientId) {
        const clientResponse = await this.$store.dispatch('fetchById', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
          id: this.job.clientId
        })

        if (OK !== clientResponse.status) {
          notifyError(this, 'Failed to load Job Client. Please refresh your screen and try again.')
          return
        }

        this.client = clientResponse.data
      }
    },
    reloadTasksWithDelay() {
      this.taskCompletedOrCancelled = true
      setTimeout(async () => {
        await this.loadTasksFromJob()
        this.taskCompletedOrCancelled = false
      }, Constants.TIME.SECONDS.THREE)
      this.$forceUpdate()
    },
    updateTask(task) {
      if (this.taskCompletedOrCancelled) {
        return
      }
      let forceUpdate = false

      this.tasks.forEach((bulkTask) => {
        if (bulkTask.id === task.id) {
          Object.keys(task).forEach(prop => {
            if (task.hasOwnProperty(prop)) {
              bulkTask[prop] = task[prop]
            }
          })

          if (task.assigneeId) {
            bulkTask.assignee = objectBy(this.users.concat(this.usersDeactivated), 'value', task.assigneeId)
            forceUpdate = true
          }

          if (task.notes) {
            bulkTask.notes = task.notes
            forceUpdate = true
          }
        }
      })

      if (forceUpdate) {
        this.$forceUpdate()
      }
    },
    async loadTasksFromJob() {
      await this.loadJobAndClient()
      const tasksResponse = await this.$store.dispatch('tasks/fetchTasksByJobId', this.data.jobId)

      if (OK === tasksResponse.status) {
        const tasks = tasksResponse.data
        tasks.forEach(task => {
          if (task.assigneeId) {
            const assignee = objectBy(this.users.concat(this.usersDeactivated), 'value', task.assigneeId)
            if (assignee && assignee.label) {
              task.assignee = assignee.label
            } else {
              notifyError(this, `Task: ${task.name} is assigned but cannot find assignee by ID: ${task.assigneeId}.`)
            }
          }
          if (task.name.startsWith(Constants.TASKS.NAMES.INVOICE_SUBMITTAL) || task.name.startsWith(Constants.TASKS.NAMES.INVOICE)) {
            if (!task.fieldValues.poNumber && this.client && this.client.poRequired) {
              notifyWarning(this, `${this.client.name} requires a PO. Please add a PO Number to each Invoice Submittal task.`)
            }

            // If the Invoice Submittal task has a poNumber, copy it to its linked task
            if (this.job.invoiceByTask && task.fieldValues.poNumber) {
              const linkedTask = objectById(tasks, task.linkedTaskId)
              if (linkedTask) {
                linkedTask.fieldValues.poNumber = task.fieldValues.poNumber
              }
            }
          }
        })
        this.tasks = tasks
      }
    },
    async maybeUpdateLinkedTaskPoNumber(invoiceSubmittalTask) {
      if (this.checkPONumbers() && this.job.invoiceByTask) {
        const linkedTask = objectById(this.tasks, invoiceSubmittalTask.linkedTaskId)
        if (linkedTask) {
          linkedTask.fieldValues.poNumber = _.get(invoiceSubmittalTask, 'fieldValues.poNumber')
          await this.saveOne(invoiceSubmittalTask)
          this.$forceUpdate()
        }
      }
    }
  },
  async beforeMount() {
    await this.$store.cache.dispatch('statuses/fetchByEntityType', 'PtracTask')
    await this.loadTasksFromJob()
  },
  mounted() {
    this.$eventHub.$on('taskSaved', this.updateTask)
    this.$eventHub.$on('appendNote', this.appendNote)
    this.$eventHub.$on('taskCompletedOrCancelled', this.reloadTasksWithDelay)
  },
  beforeDestroy() {
    this.$eventHub.$emit('bulkEditTasksClosed')
  },
  destroyed() {
    this.$eventHub.$off('taskSaved')
    this.$eventHub.$off('appendNote')
    this.$eventHub.$off('taskCompletedOrCancelled')
  },
}
</script>

<style scoped>
  .bulk-tasks {
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #ebeef5;
    color: #606266;
  }
  .bulk-tasks td {
    border: 1px solid #ebeef5;
  }
  .bulk-tasks th {
    padding: 10px;
    color: #909399;
    text-transform: uppercase;
  }
  .bulk-tasks td {
    padding: 3px;
  }
</style>
